import { TweenBlock } from "../../types";
import { i18nLanguage } from "i18n";
import { getLocalizedString } from "../../utils/getLocalizedString";
import type { ElementType } from "react";
import { normalizeRichTextHtml } from "../../utils/normalizeRichTextHtml";
import { GroupMediaCarousel } from "./GroupMediaCarousel";
import {
  Eye,
  InfoCircle,
  MapPin,
  MessageText,
  Tree,
  WarningTriangle,
} from "iconoir-react";

type TripperTweenProps = {
  item: TweenBlock;
  language: i18nLanguage;
  isActive: boolean;
};

const TweenIcon = ({
  Element,
  color = "var(--tripper-icon-normal)",
}: {
  Element: ElementType<{ width?: number | string; height?: number | string; color?: string }>;
  color?: string;
}) => <Element width={20} height={20} color={color} strokeWidth={2.0} />;

type MessageType = {
  label: string;
  Icon: ElementType<{ color?: string }>;
  badgeBg: string;
  iconColor: string;
};

const MESSAGE_TYPES: Record<string, MessageType> = {
  WARNING: {
    label: "Warning",
    Icon: ({ color }) => <TweenIcon Element={WarningTriangle} color={color} />,
    badgeBg: "#FED3AF",
    iconColor: "#BC5215",
  },
  FYI: {
    label: "FYI",
    Icon: ({ color }) => <TweenIcon Element={InfoCircle} color={color} />,
    badgeBg: "#C6DDE8",
    iconColor: "#205EA6",
  },
  SCENERY: {
    label: "Scenery",
    Icon: ({ color }) => <TweenIcon Element={Tree} color={color} />,
    badgeBg: "#DDE2B2",
    iconColor: "#66800B",
  },
  WELCOME_TO: {
    label: "Welcome",
    Icon: ({ color }) => <TweenIcon Element={MapPin} color={color} />,
    badgeBg: "#E2D9E9",
    iconColor: "#5E409D",
  },
  ATTENTION: {
    label: "Attention",
    Icon: ({ color }) => <TweenIcon Element={Eye} color={color} />,
    badgeBg: "#F6E2A0",
    iconColor: "#AD8301",
  },
  NONE: {
    label: "Note",
    Icon: ({ color }) => <TweenIcon Element={MessageText} color={color} />,
    badgeBg: "var(--wa-colors-bg-neutral-subtle, #E6E4D9)",
    iconColor: "var(--tripper-icon-subtle)",
  },
};

export const TripperTween = ({ item, language, isActive: _isActive }: TripperTweenProps) => {
  const rawContent = item.content ? getLocalizedString(item.content, language) : "";
  const content = normalizeRichTextHtml(rawContent);
  const mt = item.tweenType ?? "NONE";
  const config = MESSAGE_TYPES[mt] || MESSAGE_TYPES["NONE"];
  const Icon = config.Icon;
  const customLabel = item.tweenLabel ? getLocalizedString(item.tweenLabel, language) : "";
  const label = customLabel || (mt === "NONE" ? "" : config.label);

  return (
    <div className="tripper-message" data-item-id={item._id.toString()}>
      <div className="tripper-message__header">
        <div className="tripper-message__icon" style={{ background: config.badgeBg }}>
          <Icon color={config.iconColor} />
        </div>
        {label && <span className="tripper-message__label">{label}</span>}
      </div>
      {content && <div className="tripper-message__body" dangerouslySetInnerHTML={{ __html: content }} />}
      {item.media && <GroupMediaCarousel media={item.media} language={language} />}
      {item.link?.url && (
        <a
          href={item.link.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontWeight: 600,
            fontSize: 14,
            color: "var(--tripper-link)",
            textDecoration: "underline",
          }}
        >
          {item.link.label ? getLocalizedString(item.link.label, language) : "Learn more"}
        </a>
      )}
    </div>
  );
};
